import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useNotification } from './context/NotificationContext';

const DEFAULT_MANAGER_BASE = 'https://frek.cc/proxy/10.147.19.189:7070';
const DEFAULT_BOT_HOST = 'https://frek.cc/proxy/10.147.19.189';
const DEFAULT_SOURCES_BASE = 'https://frek.cc/proxy/10.147.19.189:6061';
const DEFAULT_BALANCE_MANAGER_BASE = 'https://frek.cc/proxy/10.147.19.189:5003';
const UPDATE_SUMMARY_WEBHOOK_URL = 'http://38.54.86.24:35978/post';

const normalizeBaseUrl = (value) => value.replace(/\/+$/, '');
const normalizeProxyUrl = (value) => {
  if (!value) {
    return value;
  }
  if (value.startsWith('https://frek.cc/proxy/')) {
    return value;
  }
  return value.replace(/^http:\/\/10\.147\.19\.189/, 'https://frek.cc/proxy/10.147.19.189');
};
const toProxyUrl = (value) => {
  if (!value) {
    return value;
  }
  if (value.startsWith('https://frek.cc/proxy/')) {
    return value;
  }
  return value.replace(/^https?:\/\//, 'https://frek.cc/proxy/');
};

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#d7d7d7',
      secondary: '#9aa3ab',
    },
    primary: {
      main: '#90caf9',
    },
    success: {
      main: '#4caf50',
    },
  },
});

const emptyServiceForm = {
  service_key: '',
  service_label: '',
  bot_token: '',
  admin_user_id: '5859150099',
  mongo_user_doc_id: 'user_data_',
  mongo_sold_doc_id: 'sold_numbers_',
  fetch_active_numbers_url: 'https://frek.cc/proxy/10.147.19.189:6061/fetch_active_numbers/',
  fetch_sms_url: 'https://frek.cc/proxy/10.147.19.189:6061/fetch_sms/',
  sms_source_url: 'https://frek.cc/proxy/10.147.19.189:5005/',
  expiry_url: 'https://frek.cc/proxy/10.147.19.189:5000/expiry/',
  track_label: '',
  flask_port: '',
  otp_price: '10',
  otp_time_window_minutes: '10',
};

function OtpServicesManager() {
  const { showNotification } = useNotification();
  const [managerBaseUrl, setManagerBaseUrl] = useState(
    localStorage.getItem('otpManagerBaseUrl') || DEFAULT_MANAGER_BASE
  );
  const [botHost, setBotHost] = useState(
    localStorage.getItem('otpBotHost') || DEFAULT_BOT_HOST
  );
  const [services, setServices] = useState({});
  const [selectedService, setSelectedService] = useState(
    localStorage.getItem('otpManagerSelectedService') || ''
  );
  const [selectedBatchServices, setSelectedBatchServices] = useState(() => {
    try {
      const saved = localStorage.getItem('otpManagerBatchServices');
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  });
  const [sourcesBaseUrl, setSourcesBaseUrl] = useState(
    localStorage.getItem('otpSourcesBaseUrl') || DEFAULT_SOURCES_BASE
  );
  const [balanceManagerBaseUrl, setBalanceManagerBaseUrl] = useState(
    localStorage.getItem('balanceManagerBaseUrl') || DEFAULT_BALANCE_MANAGER_BASE
  );
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [runtimePrice, setRuntimePrice] = useState('');
  const [runtimeWindow, setRuntimeWindow] = useState('');
  const [noOtpNumbers, setNoOtpNumbers] = useState({});
  const [users, setUsers] = useState({});
  const [userAdjustments, setUserAdjustments] = useState({});
  const [userSearch, setUserSearch] = useState('');
  const [userSortMode, setUserSortMode] = useState('balance_desc');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [shouldUpdateConfig, setShouldUpdateConfig] = useState(true);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(() => {
    const saved = localStorage.getItem('otpManagerAutoRefresh');
    return saved ? JSON.parse(saved) : true;
  });
  const [activeBatches, setActiveBatches] = useState([]);
  const [finishNumbersCount, setFinishNumbersCount] = useState(0);
  const [routesEnabled, setRoutesEnabled] = useState(false);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteService, setPendingDeleteService] = useState('');
  const [serviceForm, setServiceForm] = useState(emptyServiceForm);

  const normalizedManagerBase = useMemo(
    () => normalizeBaseUrl(managerBaseUrl),
    [managerBaseUrl]
  );
  const normalizedBotHost = useMemo(() => normalizeBaseUrl(botHost), [botHost]);
  const normalizedSourcesBase = useMemo(
    () => normalizeBaseUrl(sourcesBaseUrl),
    [sourcesBaseUrl]
  );
  const normalizedBalanceManagerBase = useMemo(
    () => normalizeBaseUrl(balanceManagerBaseUrl),
    [balanceManagerBaseUrl]
  );

  const selectedConfig = selectedService ? services[selectedService] : null;
  const selectedBotBaseUrl = useMemo(() => {
    if (!selectedConfig) {
      return '';
    }
    return `${normalizedBotHost}:${selectedConfig.flask_port}`;
  }, [normalizedBotHost, selectedConfig]);

  const showStatus = (message) => {
    setStatusMessage(message);
    setErrorMessage('');
    showNotification(message, 'success');
  };

  const showError = (message) => {
    setErrorMessage(message);
    setStatusMessage('');
    showNotification(message, 'error');
  };

  const sendUpdateSummary = async (summaryMessage) => {
    try {
      const response = await fetch(toProxyUrl(UPDATE_SUMMARY_WEBHOOK_URL), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: summaryMessage }),
      });
      if (!response.ok) {
        throw new Error(`Webhook responded with status ${response.status}`);
      }
      console.info(`Sent summary to external webhook: ${summaryMessage}`);
    } catch (error) {
      console.error(`Failed to send summary to external webhook: ${error.message}`);
    }
  };

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${normalizedManagerBase}/otp_services`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load services');
      }
      setServices(data.services || {});
      if (!selectedService && data.services) {
        const keys = Object.keys(data.services);
        if (keys.length > 0) {
          setSelectedService(keys[0]);
        }
      }
      showStatus('Services loaded.');
    } catch (error) {
      showError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, [normalizedManagerBase]);

  useEffect(() => {
    localStorage.setItem('otpManagerBaseUrl', managerBaseUrl);
  }, [managerBaseUrl]);

  useEffect(() => {
    localStorage.setItem('otpBotHost', botHost);
  }, [botHost]);

  useEffect(() => {
    localStorage.setItem('otpSourcesBaseUrl', sourcesBaseUrl);
  }, [sourcesBaseUrl]);

  useEffect(() => {
    localStorage.setItem('balanceManagerBaseUrl', balanceManagerBaseUrl);
  }, [balanceManagerBaseUrl]);

  useEffect(() => {
    localStorage.setItem('otpManagerSelectedService', selectedService);
  }, [selectedService]);

  useEffect(() => {
    localStorage.setItem('otpManagerBatchServices', JSON.stringify(selectedBatchServices));
  }, [selectedBatchServices]);

  useEffect(() => {
    localStorage.setItem('otpManagerAutoRefresh', JSON.stringify(autoRefreshEnabled));
  }, [autoRefreshEnabled]);

  useEffect(() => {
    if (!services || Object.keys(services).length === 0) {
      return;
    }
    const sanitized = selectedBatchServices.filter((key) => services[key]);
    if (sanitized.length !== selectedBatchServices.length) {
      setSelectedBatchServices(sanitized);
    }
    if (sanitized.length === 0 && selectedService && services[selectedService]) {
      setSelectedBatchServices([selectedService]);
    }
  }, [services, selectedService, selectedBatchServices]);

  const handleServiceSelect = (event) => {
    const nextService = event.target.value;
    setSelectedService(nextService);
    setRuntimePrice('');
    setRuntimeWindow('');
    setNoOtpNumbers({});
    setUsers({});
    setActiveBatches([]);
    setFinishNumbersCount(0);
    if (nextService && selectedBatchServices.length === 0) {
      setSelectedBatchServices([nextService]);
    }
  };

  const handleBatchServiceSelect = (event) => {
    const value = event.target.value;
    setSelectedBatchServices(typeof value === 'string' ? value.split(',') : value);
  };

  const handleFormChange = (field) => (event) => {
    setServiceForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const openAddDialog = () => {
    setServiceForm(emptyServiceForm);
    setAddDialogOpen(true);
  };

  const openEditDialog = (serviceKey) => {
    const config = services[serviceKey];
    if (!config) {
      return;
    }
    setServiceForm({
      service_key: serviceKey,
      service_label: config.service_label || '',
      bot_token: config.bot_token || '',
      admin_user_id: config.admin_user_id || '',
      mongo_user_doc_id: config.mongo_user_doc_id || '',
      mongo_sold_doc_id: config.mongo_sold_doc_id || '',
      fetch_active_numbers_url: config.fetch_active_numbers_url || '',
      fetch_sms_url: config.fetch_sms_url || '',
      sms_source_url: config.sms_source_url || '',
      expiry_url: config.expiry_url || '',
      track_label: config.track_label || '',
      flask_port: config.flask_port || '',
      otp_price: config.otp_price ?? '',
      otp_time_window_minutes: config.otp_time_window_minutes ?? '',
    });
    setEditDialogOpen(true);
  };

  const submitAddService = async () => {
    const payload = {
      service_label: serviceForm.service_label,
      bot_token: serviceForm.bot_token,
      admin_user_id: Number(serviceForm.admin_user_id),
      mongo_user_doc_id: serviceForm.mongo_user_doc_id || `user_data_${serviceForm.service_key}`,
      mongo_sold_doc_id: serviceForm.mongo_sold_doc_id || `sold_numbers_${serviceForm.service_key}`,
      fetch_active_numbers_url: serviceForm.fetch_active_numbers_url,
      fetch_sms_url: serviceForm.fetch_sms_url,
      sms_source_url: serviceForm.sms_source_url,
      expiry_url: serviceForm.expiry_url,
      track_label: serviceForm.track_label,
      flask_port: Number(serviceForm.flask_port),
      otp_price: Number(serviceForm.otp_price),
      otp_time_window_minutes: Number(serviceForm.otp_time_window_minutes),
    };

    try {
      const response = await fetch(
        `${normalizedManagerBase}/otp_services/${serviceForm.service_key}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to add service');
      }
      setAddDialogOpen(false);
      fetchServices();
      await refreshBalanceManagerPorts();
    } catch (error) {
      showError(error.message);
    }
  };

  const submitEditService = async () => {
    const payload = {
      service_label: serviceForm.service_label,
      bot_token: serviceForm.bot_token,
      admin_user_id: Number(serviceForm.admin_user_id),
      mongo_user_doc_id: serviceForm.mongo_user_doc_id,
      mongo_sold_doc_id: serviceForm.mongo_sold_doc_id,
      fetch_active_numbers_url: serviceForm.fetch_active_numbers_url,
      fetch_sms_url: serviceForm.fetch_sms_url,
      sms_source_url: serviceForm.sms_source_url,
      expiry_url: serviceForm.expiry_url,
      track_label: serviceForm.track_label,
      flask_port: Number(serviceForm.flask_port),
      otp_price: Number(serviceForm.otp_price),
      otp_time_window_minutes: Number(serviceForm.otp_time_window_minutes),
    };

    try {
      const response = await fetch(
        `${normalizedManagerBase}/otp_services/${serviceForm.service_key}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to update service');
      }
      setEditDialogOpen(false);
      fetchServices();
      await refreshBalanceManagerPorts();
    } catch (error) {
      showError(error.message);
    }
  };

  const deleteService = async (serviceKey) => {
    try {
      const response = await fetch(`${normalizedManagerBase}/otp_services/${serviceKey}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete service');
      }
      if (selectedService === serviceKey) {
        setSelectedService('');
      }
      fetchServices();
      await refreshBalanceManagerPorts();
    } catch (error) {
      showError(error.message);
    }
  };

  const confirmDeleteService = (serviceKey) => {
    if (!serviceKey) {
      return;
    }
    setPendingDeleteService(serviceKey);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!pendingDeleteService) {
      return;
    }
    await deleteService(pendingDeleteService);
    setDeleteDialogOpen(false);
    setPendingDeleteService('');
  };

  const fetchRuntimeSettings = async () => {
    if (!selectedBotBaseUrl) {
      return;
    }
    try {
      const [priceRes, windowRes] = await Promise.all([
        fetch(`${selectedBotBaseUrl}/update_otp_price`),
        fetch(`${selectedBotBaseUrl}/otp_time_window`),
      ]);
      const priceData = await priceRes.json();
      const windowData = await windowRes.json();
      if (priceRes.ok) {
        setRuntimePrice(priceData.current_price);
      }
      if (windowRes.ok) {
        setRuntimeWindow(windowData.current_time_window);
      }
    } catch (error) {
      showError(error.message);
    }
  };

  const updateRuntimePrice = async () => {
    if (!selectedBotBaseUrl || runtimePrice === '') {
      return;
    }
    try {
      const response = await fetch(`${selectedBotBaseUrl}/update_otp_price`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price: Number(runtimePrice) }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update price');
      }
      if (shouldUpdateConfig) {
        await fetch(`${normalizedManagerBase}/otp_services/${selectedService}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ otp_price: Number(runtimePrice) }),
        });
      }
      await sendUpdateSummary(
        `OTP price updated for ${selectedService || 'unknown_service'} to ${Number(runtimePrice)}`
      );
      showStatus('OTP price updated.');
      fetchAllSelected();
    } catch (error) {
      showError(error.message);
    }
  };

  const updateRuntimeWindow = async () => {
    if (!selectedBotBaseUrl || runtimeWindow === '') {
      return;
    }
    try {
      const response = await fetch(`${selectedBotBaseUrl}/otp_time_window`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ minutes: Number(runtimeWindow) }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update time window');
      }
      if (shouldUpdateConfig) {
        await fetch(`${normalizedManagerBase}/otp_services/${selectedService}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ otp_time_window_minutes: Number(runtimeWindow) }),
        });
      }
      await sendUpdateSummary(
        `OTP time window updated for ${selectedService || 'unknown_service'} to ${Number(runtimeWindow)} minutes`
      );
      showStatus('OTP time window updated.');
      fetchAllSelected();
    } catch (error) {
      showError(error.message);
    }
  };

  const fetchNoOtpNumbers = async () => {
    if (!selectedBotBaseUrl) {
      return;
    }
    try {
      const response = await fetch(`${selectedBotBaseUrl}/no_otp_numbers`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch no OTP numbers');
      }
      setNoOtpNumbers(data.no_otp_numbers || {});
    } catch (error) {
      showError(error.message);
    }
  };

  const clearNoOtpNumbers = async () => {
    if (!selectedBotBaseUrl) {
      return;
    }
    try {
      const response = await fetch(`${selectedBotBaseUrl}/no_otp_numbers`, {
        method: 'POST',
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to clear no OTP numbers');
      }
      setNoOtpNumbers({});
      showStatus('No-OTP numbers cleared.');
      fetchAllSelected();
    } catch (error) {
      showError(error.message);
    }
  };

  const fetchUsers = async () => {
    if (!selectedBotBaseUrl) {
      return;
    }
    try {
      const response = await fetch(`${selectedBotBaseUrl}/users`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      setUsers(data || {});
    } catch (error) {
      showError(error.message);
    }
  };


  const refreshBalanceManagerPorts = async () => {
    try {
      const response = await fetch(`${normalizedBalanceManagerBase}/refresh_service_ports`, {
        method: 'POST',
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to refresh balance manager ports');
      }
      showStatus('Balance manager ports refreshed.');
    } catch (error) {
      showError(error.message);
    }
  };

  const fetchActiveBatches = async () => {
    if (!selectedConfig?.expiry_url) {
      showError('expiry_url is missing in service config.');
      return;
    }
    try {
      const response = await fetch(normalizeProxyUrl(selectedConfig.expiry_url));
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0 && data[0].value) {
        const values = data[0].value.split(',').filter((value) => value.trim() !== '');
        setActiveBatches(values);
      } else {
        setActiveBatches([]);
      }
    } catch (error) {
      showError(error.message);
    }
  };

  const submitActiveBatches = async () => {
    const targetKeys =
      selectedBatchServices.length > 0
        ? selectedBatchServices
        : selectedService
        ? [selectedService]
        : [];
    if (targetKeys.length === 0) {
      showError('Select at least one service to update.');
      return;
    }
    const missingExpiry = targetKeys.filter((key) => !services[key]?.expiry_url);
    const validTargets = targetKeys.filter((key) => services[key]?.expiry_url);
    if (validTargets.length === 0) {
      showError('expiry_url is missing for the selected services.');
      return;
    }
    try {
      const results = await Promise.allSettled(
        validTargets.map(async (key) => {
          const response = await fetch(normalizeProxyUrl(services[key].expiry_url), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ value: activeBatches.join(',') }),
          });
          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.message || `Failed to update ${key}`);
          }
          return key;
        })
      );
      const failed = results
        .filter((result) => result.status === 'rejected')
        .map((result) => (result.status === 'rejected' ? result.reason?.message : ''));
      if (missingExpiry.length > 0 || failed.length > 0) {
        const missingText = missingExpiry.length > 0 ? `Missing expiry_url: ${missingExpiry.join(', ')}` : '';
        const failedText = failed.length > 0 ? `Failed: ${failed.join(', ')}` : '';
        showError([missingText, failedText].filter(Boolean).join(' | '));
      } else {
        showStatus(`Active Goip batches updated for ${validTargets.length} service(s).`);
      }
      fetchAllSelected();
    } catch (error) {
      showError(error.message);
    }
  };

  const toggleBatchValue = (value) => {
    setActiveBatches((current) =>
      current.includes(value) ? current.filter((batch) => batch !== value) : [...current, value]
    );
  };

  const fetchFinishNumbersCount = async () => {
    if (!selectedService) {
      return;
    }
    try {
      const response = await fetch(
        `${normalizedSourcesBase}/finish_numbers_count/${selectedService}`
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch finish numbers count');
      }
      setFinishNumbersCount(data.finish_numbers_count ?? 0);
    } catch (error) {
      showError(error.message);
    }
  };

  const resetNumberStatus = async () => {
    if (!selectedService) {
      return;
    }
    try {
      const response = await fetch(
        `${normalizedSourcesBase}/reset_number_status/${selectedService}`,
        { method: 'POST' }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset number status');
      }
      showStatus('Number status reset.');
      fetchAllSelected();
    } catch (error) {
      showError(error.message);
    }
  };

  const resetFinishNumbers = async () => {
    if (!selectedService) {
      return;
    }
    try {
      const response = await fetch(
        `${normalizedSourcesBase}/reset_finish_numbers_count/${selectedService}`,
        { method: 'POST' }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset finish numbers count');
      }
      setFinishNumbersCount(0);
      showStatus('Finish numbers count reset.');
      fetchAllSelected();
    } catch (error) {
      showError(error.message);
    }
  };

  const fetchRoutesEnabled = async () => {
    try {
      const response = await fetch(`${normalizedSourcesBase}/toggle_routes`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch routes status');
      }
      setRoutesEnabled(Boolean(data.routes_enabled));
    } catch (error) {
      showError(error.message);
    }
  };

  const toggleRoutes = async () => {
    const newStatus = !routesEnabled;
    setRoutesEnabled(newStatus);
    try {
      const response = await fetch(`${normalizedSourcesBase}/toggle_routes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enable: newStatus ? 1 : 0 }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to toggle routes');
      }
      showStatus('Routes status updated.');
      fetchAllSelected();
    } catch (error) {
      setRoutesEnabled(!newStatus);
      showError(error.message);
    }
  };

  const fetchAllSelected = useCallback(async () => {
    if (!selectedService || !selectedBotBaseUrl) {
      return;
    }
    await Promise.all([
      fetchRuntimeSettings(),
      fetchNoOtpNumbers(),
      fetchUsers(),
      fetchActiveBatches(),
      fetchFinishNumbersCount(),
      fetchRoutesEnabled(),
    ]);
  }, [
    selectedService,
    selectedBotBaseUrl,
    normalizedSourcesBase,
    selectedConfig?.expiry_url,
  ]);

  useEffect(() => {
    if (!selectedService) {
      return;
    }
    fetchAllSelected();
    if (!autoRefreshEnabled) {
      return undefined;
    }
    const intervalId = setInterval(fetchAllSelected, 5000);
    return () => clearInterval(intervalId);
  }, [selectedService, fetchAllSelected, autoRefreshEnabled]);

  const updateBalanceForUser = async (username) => {
    if (!selectedBotBaseUrl || !username) {
      return;
    }
    const amount = Number(userAdjustments[username] || 0);
    if (!amount) {
      showError('Enter a non-zero amount.');
      return;
    }
    try {
      const response = await fetch(`${selectedBotBaseUrl}/add_balance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          amount,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update balance');
      }
      setUserAdjustments((prev) => ({ ...prev, [username]: '' }));
      showStatus(`Balance updated for ${username}. New balance: ${data.new_balance}`);
      fetchUsers();
      fetchAllSelected();
    } catch (error) {
      showError(error.message);
    }
  };

  const sendBroadcast = async () => {
    if (!selectedBotBaseUrl || !broadcastMessage) {
      return;
    }
    try {
      const response = await fetch(`${selectedBotBaseUrl}/send_message_to_users_with_balance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: broadcastMessage }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to send message');
      }
      showStatus(data.message);
      fetchAllSelected();
    } catch (error) {
      showError(error.message);
    }
  };

  const sendBroadcastToWebhook = async () => {
    if (!broadcastMessage) {
      return;
    }
    await sendUpdateSummary(broadcastMessage);
    showStatus('Broadcast sent to external webhook.');
  };

  const serviceKeys = Object.keys(services);
  const usersWithBalanceCount = Object.values(users).filter(
    (info) => Number(info?.balance ?? 0) > 0
  ).length;

  return (
    <ThemeProvider theme={darkTheme}>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 3 }}>
        <Container maxWidth="xl">
          <Stack spacing={2}>
            <Paper sx={{ p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    OTP Services Manager
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage services, prices, time window, balances, and Goip batches.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="center" justifyContent="flex-end">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={autoRefreshEnabled}
                          onChange={() => setAutoRefreshEnabled((prev) => !prev)}
                          color="primary"
                        />
                      }
                      label="Auto refresh (5s)"
                    />
                    <Chip label={shouldUpdateConfig ? 'DB Sync: On' : 'DB Sync: Off'} />
                    <Button size="small" onClick={() => setShouldUpdateConfig((prev) => !prev)}>
                      Toggle Sync
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Stack spacing={2}>
                  <Paper sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      <Typography variant="h6">Configuration</Typography>
                      <Stack direction="row" spacing={1}>
                        <Button variant="contained" onClick={fetchServices} disabled={loading}>
                          Refresh Services
                        </Button>
                        <Button variant="outlined" onClick={openAddDialog}>
                          Add Service
                        </Button>
                        <Button variant="outlined" onClick={refreshBalanceManagerPorts}>
                          Refresh Balance Ports
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                  <Paper sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      <Typography variant="h6">Services</Typography>
                      <Select
                        fullWidth
                        value={selectedService}
                        onChange={handleServiceSelect}
                        displayEmpty
                        size="small"
                      >
                        <MenuItem value="">
                          <em>Select a service</em>
                        </MenuItem>
                        {serviceKeys.map((key) => (
                          <MenuItem key={key} value={key}>
                            {key}
                          </MenuItem>
                        ))}
                      </Select>
                      {selectedConfig && (
                        <Stack spacing={0.75}>
                          <Typography variant="body2" color="text.secondary">
                            Port: {selectedConfig.flask_port}
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <Button variant="outlined" onClick={() => openEditDialog(selectedService)}>
                              Edit
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => confirmDeleteService(selectedService)}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </Stack>
                      )}
                      <Divider sx={{ my: 1 }} />
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="OTP Price"
                            fullWidth
                            size="small"
                            value={runtimePrice}
                            onChange={(event) => setRuntimePrice(event.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="OTP Time Window"
                            fullWidth
                            size="small"
                            value={runtimeWindow}
                            onChange={(event) => setRuntimeWindow(event.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="contained"
                              onClick={updateRuntimePrice}
                              disabled={!selectedService}
                            >
                              Update Price
                            </Button>
                            <Button
                              variant="contained"
                              onClick={updateRuntimeWindow}
                              disabled={!selectedService}
                            >
                              Update Window
                            </Button>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Stack>
                  </Paper>
                  <Grid container spacing={2}>
                    <Grid item xs={12} lg={12}>
                      <Paper sx={{ p: 1 }}>
                        <Stack spacing={1.5}>
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Typography variant="h6">Active Goip Numbers</Typography>
                            <Button variant="outlined" onClick={toggleRoutes}>
                              Toggle Routes
                            </Button>
                          </Stack>
                          <Stack direction="row" spacing={1}>
                            <Chip label={`Finish: ${finishNumbersCount}`} />
                            <Chip label={`Routes: ${routesEnabled ? 'On' : 'Off'}`} />
                          </Stack>
                          <Divider />
                          <Stack spacing={0.5}>
                            <Typography variant="subtitle2">Update Services</Typography>
                            <Select
                              multiple
                              fullWidth
                              size="small"
                              displayEmpty
                              value={selectedBatchServices}
                              onChange={handleBatchServiceSelect}
                              renderValue={(selected) => {
                                if (selected.length === 0) {
                                  return (
                                    <Typography variant="body2" color="text.secondary">
                                      Select services to update
                                    </Typography>
                                  );
                                }
                                return (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                      <Chip key={value} size="small" label={value} />
                                    ))}
                                  </Box>
                                );
                              }}
                            >
                              {serviceKeys.map((key) => (
                                <MenuItem key={key} value={key}>
                                  <Checkbox checked={selectedBatchServices.indexOf(key) > -1} size="small" />
                                  <ListItemText
                                    primary={key}
                                    secondary={services[key]?.service_label || ''}
                                  />
                                </MenuItem>
                              ))}
                            </Select>
                          </Stack>
                          <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                              {[...Array(9)].map((_, index) => {
                                const value = `${index + 1}`;
                                return (
                                  <Chip
                                    key={`goip-${value}`}
                                    label={`G ${value}`}
                                    color={activeBatches.includes(value) ? 'primary' : 'default'}
                                    variant={activeBatches.includes(value) ? 'filled' : 'outlined'}
                                    onClick={() => toggleBatchValue(value)}
                                  />
                                );
                              })}
                              {[...Array(32)].map((_, index) => {
                                const value = `9.${index + 1}`;
                                return (
                                  <Chip
                                    key={`goip-${value}`}
                                    label={`G ${value}`}
                                    color={activeBatches.includes(value) ? 'primary' : 'default'}
                                    variant={activeBatches.includes(value) ? 'filled' : 'outlined'}
                                    onClick={() => toggleBatchValue(value)}
                                  />
                                );
                              })}
                            </Stack>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="contained"
                              onClick={submitActiveBatches}
                              disabled={selectedBatchServices.length === 0 && !selectedService}
                            >
                              Submit
                            </Button>
                            <Button variant="outlined" onClick={resetNumberStatus} disabled={!selectedService}>
                              Reset Status
                            </Button>
                            <Button variant="outlined" onClick={resetFinishNumbers} disabled={!selectedService}>
                              Reset Count
                            </Button>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <Paper sx={{ p: 2 }}>
                        <Stack spacing={1.5}>
                          <Typography variant="h6">Broadcast Message</Typography>
                          <TextField
                            label="Message to users with balance"
                            fullWidth
                            multiline
                            minRows={2}
                            value={broadcastMessage}
                            onChange={(event) => setBroadcastMessage(event.target.value)}
                          />
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="contained"
                              onClick={sendBroadcast}
                              disabled={!selectedService || !broadcastMessage.trim()}
                            >
                              Send to Users
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={sendBroadcastToWebhook}
                              disabled={!broadcastMessage.trim()}
                            >
                              Send to Group
                            </Button>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>
                </Stack>
              </Grid>

              <Grid item xs={12} md={8}>
                <Stack spacing={2}>
                  <Paper sx={{ p: 2 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                          Selected Service
                        </Typography>
                        <Typography variant="subtitle1">
                          {selectedConfig?.service_label || selectedService || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                          Users
                        </Typography>
                        <Typography variant="subtitle1">{Object.keys(users).length}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                          Users with Balance
                        </Typography>
                        <Typography variant="subtitle1">{usersWithBalanceCount}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  <Grid container spacing={2}>

                    <Grid item xs={12} lg={12}>
                      <Paper sx={{ p: 2 }}>
                        <Stack spacing={1.5}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                            <Typography variant="h6">Users & Balances</Typography>
                            <TextField
                              size="small"
                              label="Search user"
                              value={userSearch}
                              onChange={(event) => setUserSearch(event.target.value)}
                              sx={{ minWidth: 240 }}
                            />
                            <Select
                              size="small"
                              value={userSortMode}
                              onChange={(event) => setUserSortMode(event.target.value)}
                              sx={{ minWidth: 160 }}
                            >
                              <MenuItem value="balance_desc">Balance: High to Low</MenuItem>
                              <MenuItem value="balance_asc">Balance: Low to High</MenuItem>
                              <MenuItem value="name_asc">Name: A to Z</MenuItem>
                              <MenuItem value="name_desc">Name: Z to A</MenuItem>
                            </Select>
                          </Stack>
                          <Box sx={{ maxHeight: 1015, overflow: 'auto' }}>
                            <Stack spacing={1}>
                              {Object.entries(users)
                                .filter(([username]) =>
                                  username.toLowerCase().includes(userSearch.toLowerCase())
                                )
                                .sort(([nameA, infoA], [nameB, infoB]) => {
                                  const balanceA = Number(infoA.balance ?? 0);
                                  const balanceB = Number(infoB.balance ?? 0);
                                  switch (userSortMode) {
                                    case 'balance_asc':
                                      return balanceA - balanceB;
                                    case 'balance_desc':
                                      return balanceB - balanceA;
                                    case 'name_desc':
                                      return nameB.localeCompare(nameA);
                                    case 'name_asc':
                                    default:
                                      return nameA.localeCompare(nameB);
                                  }
                                })
                                .map(([username, info]) => (
                                  <Box
                                    key={username}
                                    sx={{
                                      display: 'grid',
                                      gridTemplateColumns: { xs: '1fr', md: '1.4fr 0.8fr 1fr 0.7fr' },
                                      gap: 1,
                                      alignItems: 'center',
                                      p: 1,
                                      borderRadius: 1,
                                      bgcolor: 'rgba(255,255,255,0.04)',
                                    }}
                                  >
                                    <Typography variant="body2">{username}</Typography>
                                    <Chip
                                      label={`PHP ${info.balance ?? 0}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                    <TextField
                                      label="Adjust (+/-)"
                                      size="small"
                                      value={userAdjustments[username] ?? ''}
                                      onChange={(event) =>
                                        setUserAdjustments((prev) => ({
                                          ...prev,
                                          [username]: event.target.value,
                                        }))
                                      }
                                    />
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => updateBalanceForUser(username)}
                                      disabled={!selectedService}
                                    >
                                      Apply
                                    </Button>
                                  </Box>
                                ))}
                            </Stack>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>

                    

                  </Grid>
                </Stack>
              </Grid>
            </Grid>
          </Stack>

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Service</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Service Key" value={serviceForm.service_key} onChange={handleFormChange('service_key')} />
            <TextField label="Service Label" value={serviceForm.service_label} onChange={handleFormChange('service_label')} />
            <TextField label="Bot Token" value={serviceForm.bot_token} onChange={handleFormChange('bot_token')} />
            <TextField
              label="Admin User ID"
              value={serviceForm.admin_user_id}
              onChange={handleFormChange('admin_user_id')}
            />
            <TextField
              label="Mongo User Doc ID"
              value={serviceForm.mongo_user_doc_id}
              onChange={handleFormChange('mongo_user_doc_id')}
            />
            <TextField
              label="Mongo Sold Doc ID"
              value={serviceForm.mongo_sold_doc_id}
              onChange={handleFormChange('mongo_sold_doc_id')}
            />
            <TextField
              label="Fetch Active Numbers URL"
              value={serviceForm.fetch_active_numbers_url}
              onChange={handleFormChange('fetch_active_numbers_url')}
            />
            <TextField
              label="Fetch SMS URL"
              value={serviceForm.fetch_sms_url}
              onChange={handleFormChange('fetch_sms_url')}
            />
            <TextField
              label="SMS Source URL"
              value={serviceForm.sms_source_url}
              onChange={handleFormChange('sms_source_url')}
            />
            <TextField
              label="Expiry URL"
              value={serviceForm.expiry_url}
              onChange={handleFormChange('expiry_url')}
            />
            <TextField
              label="Track Label"
              value={serviceForm.track_label}
              onChange={handleFormChange('track_label')}
            />
            <TextField
              label="Flask Port"
              value={serviceForm.flask_port}
              onChange={handleFormChange('flask_port')}
            />
            <TextField
              label="OTP Price"
              value={serviceForm.otp_price}
              onChange={handleFormChange('otp_price')}
            />
            <TextField
              label="OTP Time Window Minutes"
              value={serviceForm.otp_time_window_minutes}
              onChange={handleFormChange('otp_time_window_minutes')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitAddService}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Service</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Service Key" value={serviceForm.service_key} disabled />
            <TextField label="Service Label" value={serviceForm.service_label} onChange={handleFormChange('service_label')} />
            <TextField label="Bot Token" value={serviceForm.bot_token} onChange={handleFormChange('bot_token')} />
            <TextField
              label="Admin User ID"
              value={serviceForm.admin_user_id}
              onChange={handleFormChange('admin_user_id')}
            />
            <TextField
              label="Mongo User Doc ID"
              value={serviceForm.mongo_user_doc_id}
              onChange={handleFormChange('mongo_user_doc_id')}
            />
            <TextField
              label="Mongo Sold Doc ID"
              value={serviceForm.mongo_sold_doc_id}
              onChange={handleFormChange('mongo_sold_doc_id')}
            />
            <TextField
              label="Fetch Active Numbers URL"
              value={serviceForm.fetch_active_numbers_url}
              onChange={handleFormChange('fetch_active_numbers_url')}
            />
            <TextField
              label="Fetch SMS URL"
              value={serviceForm.fetch_sms_url}
              onChange={handleFormChange('fetch_sms_url')}
            />
            <TextField
              label="SMS Source URL"
              value={serviceForm.sms_source_url}
              onChange={handleFormChange('sms_source_url')}
            />
            <TextField
              label="Expiry URL"
              value={serviceForm.expiry_url}
              onChange={handleFormChange('expiry_url')}
            />
            <TextField
              label="Track Label"
              value={serviceForm.track_label}
              onChange={handleFormChange('track_label')}
            />
            <TextField
              label="Flask Port"
              value={serviceForm.flask_port}
              onChange={handleFormChange('flask_port')}
            />
            <TextField
              label="OTP Price"
              value={serviceForm.otp_price}
              onChange={handleFormChange('otp_price')}
            />
            <TextField
              label="OTP Time Window Minutes"
              value={serviceForm.otp_time_window_minutes}
              onChange={handleFormChange('otp_time_window_minutes')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitEditService}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Service</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Delete service "{services[pendingDeleteService]?.service_label || pendingDeleteService}"? This cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setPendingDeleteService('');
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirmed}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default OtpServicesManager;
